import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  X, Image as ImageIcon, Search, RefreshCw, Wand2, Plus, Download, Check, Copy,
} from 'lucide-react';
import { Type as TextIcon } from 'lucide-react';
import { GlassButton } from '../../ui';
import {
  PLATFORM_COPY_FIELDS,
  PLATFORM_BADGE_COLOR,
  PLATFORM_LABEL_DEFAULTS,
  resolveInitialFieldValues,
} from './socialAssetMeta';
import './SocialAssetEditor.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

// ── Types ────────────────────────────────────────────────────────────────────

export interface CopyField {
  key: string;
  label: string;
  multiline: boolean;
  limit?: number;
}

export interface SocialAssetEditorProps {
  // Identity & framing
  platform: string;
  platformLabel?: string;
  variantLabel?: string;

  // Content
  initialContent: string | Record<string, string>;
  fields?: CopyField[];
  initialTitle?: string;
  initialImageUrl?: string;
  initialImageConcept?: string;

  // Image behaviour toggles
  imageEditable?: boolean;
  persistImageImmediately?: boolean;

  // Required actions
  onSave: (payload: {
    content: string | Record<string, string>;
    title?: string;
    imageUrl?: string;
  }) => Promise<void>;
  onClose: () => void;

  // Image-source actions — all optional
  fetchPexels?: (query: string) => Promise<{ image_url: string }>;
  // When true, the Pexels query input is shown as read-only with helper text.
  // Use when fetchPexels uses server-side search terms and cannot honour a user query.
  pexelsQueryDisabled?: boolean;
  generateAI?: (prompt: string) => Promise<{ image_url: string }>;
  uploadImage?: (file: File) => Promise<{ image_url: string }>;

  // Immediate-persist hook
  onImageChangeImmediate?: (
    imageUrl: string,
    source: 'pexels' | 'ai' | 'upload' | 'url',
  ) => Promise<void>;

  // Optional regenerate-with-instructions panel (MultiChannel only)
  regenerate?: {
    onRegenerate: (instructions: string) => Promise<string>;
    loading: boolean;
  };

  // Optional cascade-scope dialog (Catalogue only)
  cascade?: {
    scopeChoice: 'all-cascade' | 'all' | 'platform' | null;
    onResolveScope: (choice: 'all-cascade' | 'all' | 'platform') => Promise<void>;
  };

  // Optional approval toggle (Catalogue only)
  // onToggle is () => void — parent owns persist (may fire async inside the closure)
  approval?: {
    status: 'pending' | 'approved' | null;
    onToggle: () => void;
  };

  // Optional overlay-modal trigger. When present, the "Add Overlay" button appears
  // in the image panel. The parent owns TextOverlayModal rendering; the component
  // only triggers onOpen and then closes itself.
  overlay?: {
    onOpen: () => void;
  };
}

// ── Component ────────────────────────────────────────────────────────────────

const SocialAssetEditor: React.FC<SocialAssetEditorProps> = ({
  platform,
  platformLabel,
  variantLabel,
  initialContent,
  fields: fieldsProp,
  initialTitle,
  initialImageUrl,
  initialImageConcept,
  imageEditable = true,
  persistImageImmediately = false,
  onSave,
  onClose,
  fetchPexels,
  pexelsQueryDisabled = false,
  generateAI,
  uploadImage,
  onImageChangeImmediate,
  regenerate,
  cascade,
  approval,
  overlay,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const overlayMouseDownTarget = useRef<EventTarget | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  // ── Parse initial content ──────────────────────────────────────────────────
  const parsedContent = useMemo<Record<string, string>>(() => {
    if (!initialContent) return {};
    if (typeof initialContent === 'object') return initialContent as Record<string, string>;
    try {
      const parsed = JSON.parse(initialContent);
      if (parsed && typeof parsed === 'object') return parsed as Record<string, string>;
    } catch { /* fall through */ }
    return { post_text: initialContent };
  }, [initialContent]);

  // ── Determine fields ───────────────────────────────────────────────────────
  const fields: CopyField[] = useMemo(() => {
    if (fieldsProp && fieldsProp.length > 0) return fieldsProp;
    return PLATFORM_COPY_FIELDS[platform] || [
      { key: 'content', label: 'Content', multiline: true },
    ];
  }, [fieldsProp, platform]);

  // Initial field values — computed once, shared by fieldValues and the
  // image seeds below. Never read parsedContent by field key directly
  // elsewhere — its plain-string fallback is always keyed `post_text`
  // regardless of platform (see resolveInitialFieldValues's docstring).
  const initialFieldValues = useMemo(
    () => resolveInitialFieldValues(initialContent, parsedContent, fields),
    [initialContent, parsedContent, fields],
  );

  // ── Field value state ──────────────────────────────────────────────────────
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(
    () => initialFieldValues,
  );

  const [title, setTitle] = useState(initialTitle || '');
  const [imageUrl, setImageUrl] = useState(initialImageUrl || '');

  // ── Derive a good default for image seeds from content ────────────────────
  const contentSeedText = useMemo(() => {
    const firstVal = fields.map(f => initialFieldValues[f.key] || '').find(v => v);
    return firstVal ? String(firstVal).slice(0, 80) : '';
  }, [fields, initialFieldValues]);

  // ── Pexels + AI prompt state ───────────────────────────────────────────────
  const [pexelsQuery, setPexelsQuery] = useState(() => {
    if (initialImageConcept) return initialImageConcept.slice(0, 80);
    return contentSeedText;
  });
  const [aiPrompt, setAiPrompt] = useState(() => {
    if (initialImageConcept) return initialImageConcept;
    return contentSeedText;
  });

  // ── Loading / UI state ─────────────────────────────────────────────────────
  const [fetchingImg, setFetchingImg] = useState(false);
  const [generatingImg, setGeneratingImg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [regenInstructions, setRegenInstructions] = useState('');
  const [showScopeDialog, setShowScopeDialog] = useState(false);
  const [scopeSaving, setScopeSaving] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  // Track whether the image has changed from its initial value
  const imageChanged = imageUrl !== (initialImageUrl || '');

  // ── Escape key handler ─────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showScopeDialog) {
        setShowScopeDialog(false);
        return;
      }
      if (saving || fetchingImg || generatingImg) return;
      onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, saving, fetchingImg, generatingImg, showScopeDialog]);

  // ── Platform display meta ──────────────────────────────────────────────────
  const displayLabel = platformLabel || PLATFORM_LABEL_DEFAULTS[platform] || platform;
  const badgeColor = PLATFORM_BADGE_COLOR[platform] || '#555';

  // ── Copy to clipboard ──────────────────────────────────────────────────────
  const copyText = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  // ── Image update helper — handles persist-immediately ─────────────────────
  const applyImageChange = async (
    url: string,
    source: 'pexels' | 'ai' | 'upload' | 'url',
  ) => {
    setImageUrl(url);
    if (persistImageImmediately && onImageChangeImmediate) {
      await onImageChangeImmediate(url, source);
    }
  };

  // ── Pexels fetch ───────────────────────────────────────────────────────────
  const handleFetchPexels = async () => {
    if (!fetchPexels) return;
    setFetchingImg(true);
    setImageError(null);
    try {
      let url: string;
      try {
        url = (await fetchPexels(pexelsQuery)).image_url;
      } catch (err) {
        setImageError(getApiError(err, 'Could not fetch an image from Pexels. Please try again.'));
        return;
      }
      if (!url) {
        setImageError('No image was returned. Please try again.');
        return;
      }
      try {
        await applyImageChange(url, 'pexels');
      } catch (err) {
        setImageError(getApiError(err, 'Image was fetched but could not be saved to this post. Please try again.'));
      }
    } finally {
      setFetchingImg(false);
    }
  };

  // ── AI image generation ────────────────────────────────────────────────────
  const handleGenerateAI = async () => {
    if (!generateAI || !aiPrompt.trim()) return;
    setGeneratingImg(true);
    setImageError(null);
    try {
      let url: string;
      try {
        url = (await generateAI(aiPrompt.trim())).image_url;
      } catch (err) {
        setImageError(getApiError(err, 'Image generation failed. Please try again.'));
        return;
      }
      if (!url) {
        setImageError('No image was returned. Please try again.');
        return;
      }
      try {
        await applyImageChange(url, 'ai');
      } catch (err) {
        setImageError(getApiError(err, 'Image was generated but could not be saved to this post. Please try again.'));
      }
    } finally {
      setGeneratingImg(false);
    }
  };

  // ── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadImage) return;
    setImageError(null);
    try {
      let url: string;
      try {
        url = (await uploadImage(file)).image_url;
      } catch (err) {
        setImageError(getApiError(err, 'Image upload failed. Please try again.'));
        return;
      }
      if (!url) {
        setImageError('No image was returned. Please try again.');
        return;
      }
      try {
        await applyImageChange(url, 'upload');
      } catch (err) {
        setImageError(getApiError(err, 'Image was uploaded but could not be saved to this post. Please try again.'));
      }
    } finally {
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  // ── Regenerate (MultiChannel) ──────────────────────────────────────────────
  const handleRegenerate = async () => {
    if (!regenerate) return;
    const newText = await regenerate.onRegenerate(regenInstructions);
    // Replace the first field's value with the regenerated text
    const firstKey = fields[0]?.key;
    if (firstKey) {
      setFieldValues(prev => ({ ...prev, [firstKey]: newText }));
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const buildContent = (): string | Record<string, string> => {
    // If original content was an object (or multi-field), return object
    if (typeof initialContent === 'object' || fields.length > 1) {
      if (typeof initialContent === 'object') {
        return { ...(initialContent as Record<string, string>), ...fieldValues };
      }
      return { ...parsedContent, ...fieldValues };
    }
    // Single-field: return string
    return fieldValues[fields[0]?.key] || '';
  };

  const handleSave = async () => {
    // If cascade is configured, image has changed, and no scope chosen yet — show dialog
    if (cascade && imageChanged && cascade.scopeChoice === null) {
      setShowScopeDialog(true);
      return;
    }
    setSaving(true);
    try {
      const content = buildContent();
      await onSave({ content, title, imageUrl: imageUrl || undefined });
      onClose();
    } finally { setSaving(false); }
  };

  const handleScopeChoice = async (choice: 'all-cascade' | 'all' | 'platform') => {
    if (!cascade) return;
    setScopeSaving(true);
    try {
      await cascade.onResolveScope(choice);
      setShowScopeDialog(false);
      // After scope is resolved, proceed with save
      setSaving(true);
      try {
        const content = buildContent();
        await onSave({ content, title, imageUrl: imageUrl || undefined });
        onClose();
      } finally { setSaving(false); }
    } finally { setScopeSaving(false); }
  };

  // ── Overlay trigger ────────────────────────────────────────────────────────
  const handleOpenOverlay = () => {
    // Signal parent to open TextOverlayModal, then close this editor.
    // Parent sets overlayModalAsset before this editor unmounts.
    overlay?.onOpen();
    onClose();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      ref={overlayRef}
      className="sae-overlay"
      onMouseDown={e => { overlayMouseDownTarget.current = e.target; }}
      onClick={e => {
        if (
          e.target === overlayRef.current &&
          overlayMouseDownTarget.current === overlayRef.current
        ) {
          onClose();
        }
      }}
    >
      <div className="sae-modal" style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>

        {/* Cascade scope dialog (rendered inside the modal frame) */}
        {showScopeDialog && (
          <div className="sae-scope-dialog">
            <div className="sae-scope-inner">
              <p className="sae-scope-title">Apply image change to…</p>
              <p className="sae-scope-note">
                Choose which posts this image change should affect.
              </p>
              <div className="sae-scope-actions">
                <GlassButton
                  variant="primary"
                  onClick={() => handleScopeChoice('all-cascade')}
                  disabled={scopeSaving}
                  style={{ width: '100%' }}
                >
                  All platforms + source article
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  onClick={() => handleScopeChoice('all')}
                  disabled={scopeSaving}
                  style={{ width: '100%' }}
                >
                  All platforms only
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  onClick={() => handleScopeChoice('platform')}
                  disabled={scopeSaving}
                  style={{ width: '100%' }}
                >
                  {displayLabel} only
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  onClick={() => setShowScopeDialog(false)}
                  disabled={scopeSaving}
                  style={{ width: '100%' }}
                >
                  Cancel
                </GlassButton>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="sae-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="sae-platform-badge" style={{ background: badgeColor }}>
              {displayLabel}
            </span>
            {variantLabel && (
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                {variantLabel}
              </span>
            )}
          </div>
          <button className="sae-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="sae-body">
          {/* Left: image panel */}
          {imageEditable ? (
            <div className="sae-image-panel">
              <div className="sae-image-frame">
                {imageUrl ? (
                  <img src={imageUrl} alt="Asset" className="sae-img" />
                ) : (
                  <div className="sae-img-empty">
                    <ImageIcon size={32} style={{ opacity: 0.25 }} />
                    <span>No image</span>
                  </div>
                )}
              </div>

              {imageError && (
                <p className="sae-image-error" role="alert">{imageError}</p>
              )}

              {/* Pexels fetch */}
              {fetchPexels && (
                <div className="sae-img-control">
                  <label className="sae-field-label">Pexels Query</label>
                  <input
                    className="sae-query-input"
                    value={pexelsQuery}
                    onChange={e => !pexelsQueryDisabled && setPexelsQuery(e.target.value)}
                    readOnly={pexelsQueryDisabled}
                    placeholder={pexelsQueryDisabled ? 'Image search uses article keywords' : 'Search term…'}
                    title={pexelsQueryDisabled ? 'Image search uses article keywords — the query cannot be customised here' : undefined}
                    style={pexelsQueryDisabled ? { opacity: 0.6, cursor: 'default' } : undefined}
                  />
                  <GlassButton
                    variant="secondary"
                    onClick={handleFetchPexels}
                    disabled={fetchingImg || !pexelsQuery.trim()}
                    style={{ width: '100%' }}
                  >
                    {fetchingImg
                      ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Fetching…</>
                      : <><Search size={13} /> Fetch from Pexels</>
                    }
                  </GlassButton>
                </div>
              )}

              {/* Divider between Pexels + AI */}
              {fetchPexels && generateAI && (
                <div className="sae-img-divider">or</div>
              )}

              {/* AI image prompt */}
              {generateAI && (
                <div className="sae-img-control">
                  <label className="sae-field-label">AI Image Prompt</label>
                  <textarea
                    className="sae-query-input"
                    value={aiPrompt}
                    onChange={e => setAiPrompt(e.target.value)}
                    placeholder="Describe the image you want generated…"
                    rows={3}
                    style={{ resize: 'vertical', minHeight: '4.5rem', fontFamily: 'inherit' }}
                  />
                  <GlassButton
                    variant="secondary"
                    onClick={handleGenerateAI}
                    disabled={generatingImg || !aiPrompt.trim()}
                    style={{ width: '100%' }}
                  >
                    {generatingImg
                      ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
                      : <><Wand2 size={13} /> Generate with AI</>
                    }
                  </GlassButton>
                </div>
              )}

              {/* Upload */}
              {uploadImage && (
                <>
                  <GlassButton
                    variant="secondary"
                    onClick={() => uploadRef.current?.click()}
                    style={{ width: '100%' }}
                  >
                    <Plus size={13} /> Upload image
                  </GlassButton>
                  <input
                    ref={uploadRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleUpload}
                  />
                </>
              )}

              {/* Download */}
              {imageUrl && (
                <a
                  href={imageUrl}
                  download={`asset-${platform}.png`}
                  className="sae-download"
                >
                  <Download size={13} /> Download image
                </a>
              )}

              {/* Add Overlay */}
              {overlay && imageUrl && (
                <GlassButton
                  variant="secondary"
                  onClick={handleOpenOverlay}
                  style={{ width: '100%' }}
                >
                  <TextIcon size={13} /> Add Overlay
                </GlassButton>
              )}
            </div>
          ) : (
            /* Read-only image panel (e.g. MultiChannel where hero is managed above) */
            <div className="sae-image-panel-readonly">
              <div className="sae-image-frame">
                {imageUrl ? (
                  <img src={imageUrl} alt="Asset" className="sae-img" />
                ) : (
                  <div className="sae-img-empty">
                    <ImageIcon size={32} style={{ opacity: 0.25 }} />
                    <span>No image</span>
                  </div>
                )}
              </div>
              <p className="sae-img-readonly-caption">
                Change hero in the Hero Image card above.
              </p>
              {imageUrl && (
                <a
                  href={imageUrl}
                  download={`asset-${platform}.png`}
                  className="sae-download"
                >
                  <Download size={13} /> Download image
                </a>
              )}
            </div>
          )}

          {/* Right: copy panel */}
          <div className="sae-copy-panel">
            {/* Title field */}
            <div className="sae-field">
              <label className="sae-field-label">Title</label>
              <input
                className="sae-query-input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Asset title…"
              />
            </div>

            {/* Platform-specific copy fields */}
            {fields.map(f => (
              <div key={f.key} className="sae-field">
                <div className="sae-field-label-row">
                  <label className="sae-field-label">{f.label}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {f.limit && (
                      <span style={{
                        fontSize: '0.72rem',
                        color: (fieldValues[f.key] || '').length > f.limit
                          ? 'var(--danger)' : 'var(--text-secondary)',
                      }}>
                        {(fieldValues[f.key] || '').length}/{f.limit}
                      </span>
                    )}
                    <button
                      className="sae-copy-btn"
                      onClick={() => copyText(fieldValues[f.key] || '', f.key)}
                      title="Copy"
                    >
                      {copied === f.key ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                </div>
                {f.multiline ? (
                  <textarea
                    className="sae-textarea"
                    value={fieldValues[f.key] || ''}
                    onChange={e =>
                      setFieldValues(prev => ({ ...prev, [f.key]: e.target.value }))
                    }
                    rows={
                      f.key === 'intro_text' ||
                      f.key === 'caption' ||
                      f.key === 'post_text' ||
                      f.key === 'tweet_text'
                        ? 12 : 8
                    }
                  />
                ) : (
                  <input
                    className="sae-query-input"
                    value={fieldValues[f.key] || ''}
                    onChange={e =>
                      setFieldValues(prev => ({ ...prev, [f.key]: e.target.value }))
                    }
                  />
                )}
              </div>
            ))}

            {/* Approval row (Catalogue) */}
            {approval && (
              <div className="sae-approval-row">
                <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                  Approval status: {approval.status === 'approved' ? 'Approved' : 'Pending'}
                </span>
                <GlassButton
                  variant={approval.status === 'approved' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => approval.onToggle()}
                >
                  {approval.status === 'approved' ? 'Unapprove' : 'Approve article'}
                </GlassButton>
              </div>
            )}
          </div>
        </div>

        {/* Regenerate panel (MultiChannel) */}
        {regenerate && (
          <div className="sae-regenerate-panel">
            <label className="sae-field-label">Regenerate with Instructions</label>
            <textarea
              className="sae-regenerate-instructions"
              value={regenInstructions}
              onChange={e => setRegenInstructions(e.target.value)}
              placeholder="e.g. make it punchier, focus on the CTA…"
              rows={2}
            />
            <GlassButton
              variant="secondary"
              onClick={handleRegenerate}
              disabled={regenerate.loading || !regenInstructions.trim()}
              style={{ alignSelf: 'flex-end' }}
            >
              {regenerate.loading
                ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Regenerating…</>
                : <><RefreshCw size={13} /> Regenerate</>
              }
            </GlassButton>
          </div>
        )}

        {/* Footer */}
        <div className="sae-footer">
          <GlassButton variant="secondary" onClick={onClose}>Cancel</GlassButton>
          <GlassButton variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

export default SocialAssetEditor;
